import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  WITHDRAWAL_METHODS,
  WITHDRAWAL_STATUS_META,
  decideWithdrawal,
  listAllWithdrawals,
  type WithdrawalRequest,
  type WithdrawalStatus,
} from '@/lib/withdrawals';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Demandes de retrait des commerces.
// Le SuperAdmin approuve ou refuse, avec une explication obligatoire
// en cas de refus (le montant est alors automatiquement remboursé).
// ─────────────────────────────────────────────────────────────

type Filter = 'pending' | 'all' | 'approved' | 'rejected';

function formatDate(value: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function methodLabel(method: string): string {
  return WITHDRAWAL_METHODS.find((m) => m.value === method)?.label || method;
}

export default function SuperAdminWithdrawalsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<WithdrawalRequest[]>([]);
  const [names, setNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('pending');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [active, setActive] = useState<{ row: WithdrawalRequest; action: 'approved' | 'rejected' } | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listAllWithdrawals(300);
      setRows(list);

      const ids = Array.from(new Set(list.map((r) => r.userId).filter((id) => id > 0)));
      if (ids.length > 0) {
        const { data } = await supabase.from('users').select('id, name, user_name, email').in('id', ids);
        const map: Record<number, string> = {};
        ((data || []) as { id: number; name: string | null; user_name: string | null; email: string | null }[])
          .forEach((u) => { map[Number(u.id)] = u.name || u.user_name || u.email || `Commerce #${u.id}`; });
        setNames(map);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((r) => r.status === (filter as WithdrawalStatus));
  }, [rows, filter]);

  const pendingTotal = useMemo(
    () => rows.filter((r) => r.status === 'pending').reduce((s, r) => s + r.amount, 0),
    [rows],
  );

  const decide = async () => {
    if (!active) return;
    setSaving(true);
    setError('');
    const result = await decideWithdrawal({
      id: active.row.id,
      status: active.action,
      adminNote,
      adminId: user?.id ?? null,
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error || 'Action impossible.');
      return;
    }
    setNotice(active.action === 'approved'
      ? 'Demande approuvée. Le commerce a été informé.'
      : 'Demande refusée. Le montant a été remboursé au commerce.');
    setActive(null);
    setAdminNote('');
    await load();
    window.setTimeout(() => setNotice(''), 4000);
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
          <i className="ri-bank-card-2-line mr-2 text-primary-500"></i>Retraits commerces
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Validez ou refusez les demandes de retrait du portefeuille Zifek.
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
          <button onClick={() => setError('')} className="ml-auto cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}
      {notice && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-accent-50 text-accent-700 text-sm flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>{notice}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Demandes en attente</p>
          <p className="text-lg font-bold font-heading text-foreground-950">
            {rows.filter((r) => r.status === 'pending').length}
          </p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Montant en attente</p>
          <p className="text-lg font-bold font-heading text-foreground-950">
            {pendingTotal.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <p className="text-[11px] font-medium text-foreground-500 uppercase tracking-wide">Total demandes</p>
          <p className="text-lg font-bold font-heading text-foreground-950">{rows.length}</p>
        </div>
      </div>

      <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1 w-fit mb-4">
        {([
          { key: 'pending', label: 'En attente' },
          { key: 'approved', label: 'Approuvées' },
          { key: 'rejected', label: 'Refusées' },
          { key: 'all', label: 'Toutes' },
        ] as { key: Filter; label: string }[]).map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              filter === f.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-lg py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-inbox-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">Aucune demande dans cette catégorie.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const meta = WITHDRAWAL_STATUS_META[r.status];
            return (
              <div key={r.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                    <i className="ri-store-3-line"></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground-900 truncate">
                      {names[r.userId] || `Commerce #${r.userId}`}
                    </p>
                    <p className="text-[11px] text-foreground-500">
                      {methodLabel(r.method)} · {formatDate(r.createdAt)}
                    </p>
                  </div>
                  <span className="text-base font-bold text-foreground-950 whitespace-nowrap">
                    {r.amount.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {r.currency}
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap ${meta.className}`}>
                    {meta.label}
                  </span>
                </div>

                {r.destination && (
                  <p className="text-[11px] text-foreground-600 mt-2 break-all">
                    <i className="ri-arrow-right-s-line"></i> Destination : {r.destination}
                  </p>
                )}
                {r.note && (
                  <p className="text-[11px] text-foreground-500 mt-1 break-words">Note du commerce : {r.note}</p>
                )}
                {r.adminNote && (
                  <p className="text-[11px] mt-1.5 px-2 py-1.5 rounded-md bg-background-100 text-foreground-600">
                    <i className="ri-chat-1-line mr-1"></i>{r.adminNote}
                  </p>
                )}

                {r.status === 'pending' && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => { setActive({ row: r, action: 'approved' }); setAdminNote(''); }}
                      className="px-4 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-check-line mr-1"></i>Approuver
                    </button>
                    <button
                      type="button"
                      onClick={() => { setActive({ row: r, action: 'rejected' }); setAdminNote(''); }}
                      className="px-4 py-2 rounded-full bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-close-line mr-1"></i>Refuser
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modale de décision */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md bg-background-50 rounded-2xl border border-background-200/70 p-5">
            <div className="flex items-start gap-2.5 mb-3">
              <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                active.action === 'approved' ? 'bg-accent-100 text-accent-700' : 'bg-red-50 text-red-600'
              }`}>
                <i className={active.action === 'approved' ? 'ri-check-line' : 'ri-close-line'}></i>
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold font-heading text-foreground-950">
                  {active.action === 'approved' ? 'Approuver le retrait' : 'Refuser le retrait'}
                </p>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {active.row.amount.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {active.row.currency} —{' '}
                  {names[active.row.userId] || `Commerce #${active.row.userId}`}
                </p>
              </div>
            </div>

            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
              {active.action === 'rejected' ? 'Motif du refus (obligatoire)' : 'Message au commerce (facultatif)'}
            </label>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={active.action === 'rejected'
                ? 'Ex : le numéro Mobile Money ne correspond pas au titulaire du compte.'
                : 'Ex : le virement sera effectué sous 48 h ouvrées.'}
              className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
            ></textarea>

            {active.action === 'rejected' && (
              <p className="text-[11px] text-foreground-500 mt-2">
                Le montant bloqué sera automatiquement remboursé sur le portefeuille du commerce.
              </p>
            )}

            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => { void decide(); }}
                disabled={saving || (active.action === 'rejected' && adminNote.trim().length < 3)}
                className={`flex-1 py-2.5 rounded-full text-sm font-semibold text-background-50 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap ${
                  active.action === 'approved' ? 'bg-accent-500 hover:bg-accent-600' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : 'Confirmer'}
              </button>
              <button
                type="button"
                onClick={() => { setActive(null); setAdminNote(''); }}
                className="px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}